import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomTypography from "../../common/form/CustomTypography";
import FormSmallTextField from "../../common/form/FormTextField";
import AppButton from "../../common/buttons/AppButton";
import { TaskComment } from "../../../../models/TaskTracker/TaskComment";
import { TaskCommentAttachment } from "../../../../models/TaskTracker/TaskCommentAttachment";



export const getInitials = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Deterministic avatar colour so each person keeps a consistent, distinct hue.
const AVATAR_COLORS = [
  "#5E35B1",
  "#1E88E5",
  "#00897B",
  "#E53935",
  "#FB8C00",
  "#8E24AA",
  "#3949AB",
  "#00ACC1",
  "#43A047",
  "#F4511E",
  "#6D4C41",
  "#D81B60",
];

export const avatarColor = (name?: string): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const formatActivityTime = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface UserAvatarProps {
  name?: string;
  size?: number;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ name, size = 36 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.4,
      fontWeight: 600,
      bgcolor: avatarColor(name),
      flexShrink: 0,
    }}
  >
    {getInitials(name)}
  </Avatar>
);



interface CommentComposerProps {
  placeholder?: string;
  submitLabel?: string;
  busy?: boolean;
  autoFocus?: boolean;
  showAvatar?: boolean;
  avatarName?: string;
  onSubmit: (message: string, files: File[]) => Promise<void> | void;
  onCancel?: () => void;
}

// Limited to what the upload endpoint (/v5/images/product/multiple) accepts.
const ACCEPTED_TYPES =
  "image/png,image/jpeg,image/jpg,image/webp,application/pdf";

export const CommentComposer: React.FC<CommentComposerProps> = ({
  placeholder = "Add a comment...",
  submitLabel = "Comment",
  busy = false,
  autoFocus = false,
  showAvatar = false,
  avatarName,
  onSubmit,
  onCancel,
}) => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canSubmit = message.trim().length > 0 && !busy;

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (selected.length > 0) setFiles((prev) => [...prev, ...selected]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit(message.trim(), files);
    setMessage("");
    setFiles([]);
  };

  const body = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          backgroundColor: theme.palette.background.paper,
          transition: "border-color .15s ease, box-shadow .15s ease",
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
        }}
      >
        <FormSmallTextField
          multiline
          rows={2}
          variant="standard"
          placeholder={placeholder}
          value={message}
          onChange={(_id, value) => setMessage(value || "")}
          focused={autoFocus}
          fontSize="14px"
          marginBottom={0}
          sx={{
            "& .MuiInput-root:before": { borderBottom: "none" },
            "& .MuiInput-root:after": { borderBottom: "none" },
            "& .MuiInput-root:hover:not(.Mui-disabled):before": {
              borderBottom: "none",
            },
          }}
        />

        {files.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {files.map((file, idx) => (
              <Chip
                key={`${file.name}-${idx}`}
                label={file.name}
                size="small"
                onDelete={() => removeFile(idx)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tooltip title="Attach images or PDF">
            <span>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
                sx={{ color: theme.palette.customColors?.greyTextColor }}
              >
                <AttachFileIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          {onCancel && (
            <AppButton
              variant="grayscale"
              label="Cancel"
              onClick={onCancel}
              customStyles={{ minHeight: "32px", fontSize: "0.75rem", px: 1.5 }}
            />
          )}
          <AppButton
            variant="primary"
            label={submitLabel}
            disabled={!canSubmit}
            onClick={handleSubmit}
            customStyles={{ minHeight: "32px", fontSize: "0.75rem", px: 2 }}
          />

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={ACCEPTED_TYPES}
            onChange={handleFilesSelected}
          />
        </Box>
      </Box>
    </Box>
  );

  if (!showAvatar) return body;

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      <UserAvatar name={avatarName} />
      {body}
    </Box>
  );
};



interface CommentItemProps {
  comment: TaskComment;
  currentUserId?: number;
  busy?: boolean;
  onEdit: (id: number, message: string) => Promise<void> | void;
  onDelete: (id: number) => Promise<void> | void;
}

// Attachments are shown as a downloadable filename row only (no image preview).
const AttachmentList: React.FC<{ attachments?: TaskCommentAttachment[] }> = ({
  attachments,
}) => {
  const theme = useTheme();
  if (!attachments || attachments.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1 }}>
      {attachments.map((att) => {
        const name = att.file_path.split("/").pop() || "attachment";
        const key = att.id ?? att.file_path;
        return (
          <Box
            key={key}
            component="a"
            href={att.file_path}
            target="_blank"
            rel="noreferrer"
            download={name}
            title={`Download ${name}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              maxWidth: "fit-content",
              px: 1,
              py: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              textDecoration: "none",
              color: theme.palette.text.primary,
              transition: "background-color .15s ease, color .15s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            <InsertDriveFileOutlinedIcon
              sx={{
                fontSize: 18,
                color: theme.palette.customColors?.greyTextColor,
              }}
            />
            <CustomTypography
              variant="caption"
              style={{ wordBreak: "break-all" }}
            >
              {name}
            </CustomTypography>
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
        );
      })}
    </Box>
  );
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserId,
  busy = false,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.message || "");

  const isOwn = Boolean(currentUserId && comment.admin_id === currentUserId);
  const isDeleted = comment.active === 0;
  const wasEdited = Boolean(comment.is_edited) && !isDeleted;

  const bubbleBg = isDeleted
    ? theme.palette.action.hover
    : isOwn
      ? alpha(theme.palette.primary.main, 0.06)
      : theme.palette.grey[50];
  const bubbleBorder = isDeleted
    ? theme.palette.divider
    : isOwn
      ? alpha(theme.palette.primary.main, 0.25)
      : theme.palette.divider;

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === comment.message) {
      setIsEditing(false);
      return;
    }
    await onEdit(comment.id!, trimmed);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        py: 1,
        // Reveal the action toolbar when hovering anywhere on the comment.
        "&:hover .comment-actions": { opacity: 1 },
      }}
    >
      <UserAvatar name={comment.admin_name} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            position: "relative",
            border: `1px solid ${bubbleBorder}`,
            backgroundColor: bubbleBg,
            borderRadius: 2,
            px: 1.75,
            py: 1.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography variant="body2" fontWeight={600}>
              {comment.admin_name || "Unknown"}
            </CustomTypography>
            {isOwn && !isDeleted && (
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.6,
                  px: 0.75,
                  borderRadius: 1,
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                }}
              >
                You
              </Box>
            )}
            <CustomTypography
              variant="caption"
              color={theme.palette.customColors?.greyTextColor}
            >
              {isDeleted
                ? formatActivityTime(comment.updated_date)
                : formatActivityTime(comment.created_date)}
              {wasEdited ? " · edited" : ""}
            </CustomTypography>
          </Box>

          {isDeleted ? (
            <CustomTypography
              variant="body2"
              style={{
                marginTop: 4,
                fontStyle: "italic",
                lineHeight: 1.55,
              }}
              color={theme.palette.customColors?.greyTextColor}
            >
              This message was deleted
            </CustomTypography>
          ) : isEditing ? (
            <Box sx={{ mt: 1 }}>
              <FormSmallTextField
                multiline
                rows={3}
                value={editText}
                onChange={(_id, value) => setEditText(value || "")}
                focused
                fontSize="14px"
                marginBottom={0}
                borderColor={theme.palette.customColors?.greyTextColor}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <AppButton
                  variant="primary"
                  label="Save"
                  disabled={busy}
                  onClick={handleSaveEdit}
                  customStyles={{ minHeight: "32px", fontSize: "0.75rem", px: 2 }}
                />
                <AppButton
                  variant="grayscale"
                  label="Cancel"
                  onClick={() => {
                    setEditText(comment.message || "");
                    setIsEditing(false);
                  }}
                  customStyles={{ minHeight: "32px", fontSize: "0.75rem", px: 1.5 }}
                />
              </Box>
            </Box>
          ) : (
            <>
              <CustomTypography
                variant="body2"
                style={{
                  marginTop: 4,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.55,
                }}
              >
                {comment.message}
              </CustomTypography>
              <AttachmentList attachments={comment.attachments} />
            </>
          )}

          {!isEditing && !isDeleted && isOwn && (
            <Box
              className="comment-actions"
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                display: "flex",
                gap: 0.25,
                opacity: 0,
                transition: "opacity .15s ease",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
                px: 0.25,
              }}
            >
              <ActionButton
                title="Edit"
                icon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setEditText(comment.message || "");
                  setIsEditing(true);
                }}
              />
              <ActionButton
                title="Delete"
                icon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={() => onDelete(comment.id!)}
                danger
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface ActionButtonProps {
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  title,
  icon,
  onClick,
  danger = false,
}) => {
  const theme = useTheme();
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          color: theme.palette.customColors?.greyTextColor,
          "&:hover": {
            color: danger
              ? theme.palette.error.main
              : theme.palette.primary.main,
            backgroundColor: alpha(
              danger ? theme.palette.error.main : theme.palette.primary.main,
              0.08
            ),
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export default CommentItem;
